import { Inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { AlertToastService, WINDOW } from '@services';
import { ConfirmDialogModernConfig } from '@shared/components';
import { ConfirmDialogModernService } from '@shared/components';
import { isNullish } from '@shared/utils';

@Injectable({
  providedIn: 'root',
})
export class PrintService {
  constructor(
    @Inject(WINDOW) private window: Window,
    @Inject(DOCUMENT) private document: Document,
    private confirmService: ConfirmDialogModernService,
    private alertToastService: AlertToastService,
  ) {}

  getPDFFromBase64(base64String: string): Blob | null {
    if (isNullish(base64String)) {
      return null;
    }
    // 1. Remove the data header if it exists (e.g., "data:application/pdf;base64,")
    const cleanBase64 = base64String.replace(/^data:application\/pdf;base64,/, '');

    // 2. Convert Base64 to binary
    const byteCharacters = atob(cleanBase64);
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);

    // 3. Create a Blob from the byte array
    return new Blob([byteArray], { type: 'application/pdf' });
  }

  viewPDF(blob: Blob): void {
    if (isNullish(blob)) {
      this.alertToastService.pushError('Не вдалося згенерувати PDF-файл. Спробуйте ще раз пізніше.');
      return;
    }

    this.window.open(URL.createObjectURL(blob), '_blank');
  }

  openBlob(data, type) {
    const file = new Blob([data], {
      type: type.indexOf('pdf') === 0 ? 'application/pdf' : 'text/html',
    });

    const url = URL.createObjectURL(file);
    const newTab = this.window.open(url, '_blank');

    if (!newTab) {
      const filename = this.formatFilename('', type);

      const cancelConfig: ConfirmDialogModernConfig = {
        title: 'Вікно з файлом для друку заблоковано',
        text: `Для доступу до всіх функцій сайту ваш браузер повинен дозволяти спливаючі вікна, а будь-які розширення для блокування реклами мають бути вимкнені.
               </br></br>Щоб зберегти файл на пристрій, натисніть кнопку \"Завантажити PDF\".`,
        confirmBtnText: 'Завантажити PDF',
        isHtml: true,
        cancelBtnText: 'Закрити',
        isAsync: true,
        hasLoading: true,
        callback: () => {
          this.confirmService.close();
          this.saveBlobAsFile(data, filename);
        },
      };

      this.confirmService.confirm(cancelConfig);
    }
  }

  private saveBlobAsFile(data: ArrayBuffer, fileName: string): void {
    const a = this.document.createElement('a');
    this.document.body.appendChild(a);
    a.setAttribute('style', 'display: none');

    const blob = new Blob([data as BlobPart]);
    const url = (this.window as any).URL.createObjectURL(blob);
    a.href = url;
    a.download = fileName;
    a.click();
    (this.window as any).URL.revokeObjectURL(url);
    try {
      this.document.body.removeChild(a);
    } catch (e) {
      console.warn('Error occurs on attempt to remove appended element');
    }
  }

  private formatFilename(type: string, extension: string): string {
    const date = new Date();
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // months are zero indexed
    const year = String(date.getFullYear()).substr(-2);
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    const formattedDate = `${day}-${month}-${year}_${hours}-${minutes}-${seconds}`;

    return `${formattedDate}.${extension}`;
  }
}
